import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Plus, Trash, Sword, Users, User as UserIcon, Skull, GameController } from '@phosphor-icons/react';
import BottomNav from '../components/BottomNav';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => axios.create({ baseURL: API, withCredentials: true });

const SYSTEM_LABELS = { '40k': 'Warhammer 40,000', aos: 'Age of Sigmar' };

const EMPTY_ROSTER = {
  name: '',
  system: '40k',
  faction: '',
  subfaction: '',
  points_limit: 2000,
  units: [],
  notes: '',
  is_shared: false,
};

export default function WargameRostersPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('mine');
  const [rosters, setRosters] = useState([]);
  const [ref, setRef] = useState({ systems: ['40k', 'aos'], factions: { '40k': [], aos: [] } });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [systemFilter, setSystemFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const [r, list] = await Promise.all([
        ax().get('/api/wargame-rosters/reference'),
        ax().get(tab === 'mine' ? '/api/wargame-rosters' : '/api/wargame-rosters/shared'),
      ]);
      setRef(r.data);
      setRosters(Array.isArray(list.data) ? list.data : []);
    } catch (e) {
      toast.error('Failed to load rosters');
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const filtered = systemFilter === 'all' ? rosters : rosters.filter(r => r.system === systemFilter);

  return (
    <div className="app-container" data-testid="wargame-rosters-page">
      <header className="p-6 pb-3">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center" data-testid="back-button">
            <ArrowLeft size={20} weight="bold" className="text-white" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              <Skull size={24} weight="duotone" className="text-[#DC2626]" />
              Warhammer Rosters
            </h1>
            <p className="text-[#71717A] text-xs">40K & Age of Sigmar — private by default</p>
          </div>
        </div>

        <div className="flex gap-2 mb-3">
          <button onClick={() => setTab('mine')} className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all ${tab === 'mine' ? 'bg-[#DC2626] text-white' : 'bg-[#181824] text-[#A1A1AA] border border-white/5'}`} data-testid="tab-mine">
            <UserIcon size={14} weight="bold" className="inline mr-1" /> Mine
          </button>
          <button onClick={() => setTab('shared')} className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all ${tab === 'shared' ? 'bg-[#DC2626] text-white' : 'bg-[#181824] text-[#A1A1AA] border border-white/5'}`} data-testid="tab-shared">
            <Users size={14} weight="bold" className="inline mr-1" /> Shared
          </button>
        </div>

        <div className="flex gap-2 mb-3">
          {['all', '40k', 'aos'].map(s => (
            <button key={s} onClick={() => setSystemFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${systemFilter === s ? 'bg-[#DC2626] text-white' : 'bg-[#181824] text-[#A1A1AA] border border-white/5'}`}
              data-testid={`filter-${s}`}>
              {s === 'all' ? 'All' : SYSTEM_LABELS[s]}
            </button>
          ))}
        </div>

        {tab === 'mine' && (
          <button onClick={() => setEditing('new')} className="w-full py-3 rounded-xl bg-gradient-to-r from-[#DC2626] to-[#7F1D1D] text-white font-semibold text-sm flex items-center justify-center gap-2" data-testid="new-roster-btn">
            <Plus size={16} weight="bold" /> New Roster
          </button>
        )}
      </header>

      <div className="px-6 pb-36 space-y-3">
        {loading ? (
          [1, 2].map(i => <div key={i} className="h-24 bg-white/[0.03] rounded-xl animate-pulse" />)
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <Skull size={40} weight="duotone" className="text-[#333] mx-auto mb-2" />
            <p className="text-[#71717A] text-sm">
              {tab === 'mine' ? 'No rosters yet — build your first army!' : 'No shared rosters yet'}
            </p>
          </div>
        ) : (
          filtered.map(r => <RosterCard key={r.id} roster={r} onClick={() => setEditing(r)} isMine={tab === 'mine'} />)
        )}
      </div>

      <AnimatePresence>
        {editing && (
          <EditRosterModal
            initial={editing === 'new' ? EMPTY_ROSTER : editing}
            isNew={editing === 'new'}
            ref_={ref}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}
      </AnimatePresence>

      <BottomNav />
    </div>
  );
}

function RosterCard({ roster, onClick, isMine }) {
  const overLimit = (roster.total_points || 0) > (roster.points_limit || 0);
  return (
    <button onClick={onClick} className="w-full text-left bg-[#0e0e16] border border-white/[0.06] rounded-xl p-4 hover:border-[#DC2626]/30 transition-all" data-testid={`roster-${roster.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <p className="text-white font-semibold text-base">{roster.name}</p>
          <p className="text-[#A1A1AA] text-xs">
            {SYSTEM_LABELS[roster.system]} · {roster.faction}{roster.subfaction ? ` · ${roster.subfaction}` : ''}
          </p>
          {!isMine && roster.owner_name && <p className="text-[#666] text-[10px] mt-0.5">by {roster.owner_name}</p>}
        </div>
        {roster.is_shared && (
          <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#39FF14]/10 text-[#39FF14] border border-[#39FF14]/30 uppercase tracking-wider">Shared</span>
        )}
      </div>
      <div className="flex items-center gap-3 text-xs">
        <span className={`flex items-center gap-1 ${overLimit ? 'text-[#EF4444]' : 'text-[#39FF14]'}`}>
          <GameController size={12} weight="fill" /> {roster.total_points || 0}/{roster.points_limit} pts
        </span>
        <span className="flex items-center gap-1 text-[#A1A1AA]"><Sword size={12} /> {(roster.units || []).length} units</span>
      </div>
    </button>
  );
}

function EditRosterModal({ initial, isNew, ref_, onClose, onSaved }) {
  const [form, setForm] = useState({ ...initial, units: [...(initial.units || [])] });
  const [saving, setSaving] = useState(false);

  const totalPts = (form.units || []).reduce((s, u) => s + Number(u.points || 0), 0);

  const save = async () => {
    if (!form.name.trim() || !form.faction) {
      toast.error('Name and faction are required');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await ax().post('/api/wargame-rosters', form);
        toast.success('Roster created');
      } else {
        await ax().patch(`/api/wargame-rosters/${form.id}`, form);
        toast.success('Roster saved');
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${form.name}"?`)) return;
    try {
      await ax().delete(`/api/wargame-rosters/${form.id}`);
      toast.success('Deleted');
      onSaved();
    } catch (e) { toast.error('Delete failed'); }
  };

  const addUnit = () => setForm(f => ({
    ...f,
    units: [...f.units, { name: '', model_count: 1, points: 0, wargear: [], role: '', notes: '' }],
  }));

  const updateUnit = (idx, patch) => setForm(f => ({
    ...f,
    units: f.units.map((u, i) => (i === idx ? { ...u, ...patch } : u)),
  }));

  const removeUnit = (idx) => setForm(f => ({ ...f, units: f.units.filter((_, i) => i !== idx) }));

  const addWargear = (unitIdx, name) => {
    if (!name.trim()) return;
    updateUnit(unitIdx, { wargear: [...(form.units[unitIdx].wargear || []), { name: name.trim() }] });
  };

  const removeWargear = (unitIdx, wgIdx) => {
    updateUnit(unitIdx, { wargear: form.units[unitIdx].wargear.filter((_, i) => i !== wgIdx) });
  };

  const factions = ref_.factions[form.system] || [];

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-[480px] bg-[#0e0e16] border-t border-white/10 rounded-t-2xl p-5 max-h-[92vh] overflow-y-auto"
        data-testid="roster-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold text-base flex items-center gap-2">
            <Skull size={20} weight="duotone" className="text-[#DC2626]" />
            {isNew ? 'New Roster' : form.name || 'Edit Roster'}
          </h3>
          <button onClick={onClose} className="text-[#555] hover:text-white text-sm">Cancel</button>
        </div>

        <div className="space-y-3">
          <input className="input-field" placeholder="Roster name (e.g., 'Tournament List v3')" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="input-name" />

          <div className="grid grid-cols-2 gap-2">
            <select className="input-field" value={form.system} onChange={e => setForm({ ...form, system: e.target.value, faction: '' })} data-testid="select-system">
              <option value="40k">Warhammer 40K</option>
              <option value="aos">Age of Sigmar</option>
            </select>
            <select className="input-field" value={form.faction} onChange={e => setForm({ ...form, faction: e.target.value })} data-testid="select-faction">
              <option value="">Faction…</option>
              {factions.map(f => <option key={f}>{f}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <input className="input-field" placeholder="Subfaction (optional)" value={form.subfaction || ''} onChange={e => setForm({ ...form, subfaction: e.target.value })} data-testid="input-subfaction" />
            <div>
              <p className="text-[#71717A] text-[10px] uppercase tracking-wider mb-1">Points limit</p>
              <input type="number" min="0" max="10000" className="input-field w-full text-center" value={form.points_limit} onChange={e => setForm({ ...form, points_limit: Number(e.target.value || 0) })} data-testid="input-points-limit" />
            </div>
          </div>

          <div className="pt-2 pb-1 flex items-center justify-between">
            <p className="text-[#A1A1AA] text-xs uppercase tracking-wider">Units</p>
            <span className={`text-sm font-semibold ${totalPts > form.points_limit ? 'text-[#EF4444]' : 'text-[#39FF14]'}`} data-testid="total-points">
              {totalPts}/{form.points_limit} pts
            </span>
          </div>

          {(form.units || []).map((u, idx) => (
            <UnitEditor key={idx} unit={u} idx={idx}
              onChange={(patch) => updateUnit(idx, patch)}
              onRemove={() => removeUnit(idx)}
              onAddWargear={(name) => addWargear(idx, name)}
              onRemoveWargear={(wgIdx) => removeWargear(idx, wgIdx)}
            />
          ))}

          <button onClick={addUnit} className="w-full py-2.5 rounded-xl bg-[#181824] border border-dashed border-white/10 text-[#A1A1AA] text-sm flex items-center justify-center gap-2 hover:text-white hover:border-[#DC2626]/30" data-testid="add-unit-btn">
            <Plus size={14} /> Add Unit
          </button>

          <textarea className="input-field w-full" rows="2" placeholder="Roster notes…" value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="input-notes" />

          <label className="flex items-center gap-2 py-2 cursor-pointer">
            <input type="checkbox" checked={!!form.is_shared} onChange={e => setForm({ ...form, is_shared: e.target.checked })} className="w-4 h-4 accent-[#DC2626]" data-testid="toggle-share" />
            <span className="text-white text-sm">Share with community</span>
          </label>

          <div className="flex gap-2 pt-3 sticky bottom-0 bg-[#0e0e16] pb-1">
            {!isNew && (
              <button onClick={remove} className="px-3 py-3 rounded-xl bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-sm" data-testid="delete-roster-btn">
                <Trash size={16} />
              </button>
            )}
            <button onClick={save} disabled={saving} className="flex-1 py-3 rounded-xl bg-gradient-to-r from-[#DC2626] to-[#7F1D1D] text-white font-semibold text-sm disabled:opacity-50" data-testid="save-roster-btn">
              {saving ? 'Saving…' : (isNew ? 'Create Roster' : 'Save Changes')}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function UnitEditor({ unit, idx, onChange, onRemove, onAddWargear, onRemoveWargear }) {
  const [wg, setWg] = useState('');
  return (
    <div className="p-3 rounded-xl bg-[#181824] border border-white/5 space-y-2" data-testid={`unit-${idx}`}>
      <div className="flex gap-2">
        <input className="input-field flex-1" placeholder="Unit name" value={unit.name} onChange={e => onChange({ name: e.target.value })} data-testid={`unit-${idx}-name`} />
        <button onClick={onRemove} className="text-[#555] hover:text-[#EF4444] px-2" data-testid={`unit-${idx}-remove`}><Trash size={14} /></button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <p className="text-[#71717A] text-[9px] uppercase tracking-wider">Models</p>
          <input type="number" min="1" max="200" className="input-field w-full text-center text-sm" value={unit.model_count} onChange={e => onChange({ model_count: Number(e.target.value || 1) })} data-testid={`unit-${idx}-models`} />
        </div>
        <div>
          <p className="text-[#71717A] text-[9px] uppercase tracking-wider">Points</p>
          <input type="number" min="0" className="input-field w-full text-center text-sm" value={unit.points} onChange={e => onChange({ points: Number(e.target.value || 0) })} data-testid={`unit-${idx}-points`} />
        </div>
        <div>
          <p className="text-[#71717A] text-[9px] uppercase tracking-wider">Role</p>
          <input className="input-field w-full text-center text-sm" placeholder="HQ, Troops…" value={unit.role || ''} onChange={e => onChange({ role: e.target.value })} data-testid={`unit-${idx}-role`} />
        </div>
      </div>
      <div>
        <div className="flex flex-wrap gap-1 mb-1">
          {(unit.wargear || []).map((w, wgIdx) => (
            <span key={wgIdx} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#DC2626]/10 border border-[#DC2626]/30 text-[#DC2626] text-[11px]">
              {w.name}
              <button onClick={() => onRemoveWargear(wgIdx)} className="text-[#DC2626]/70 hover:text-[#DC2626]" data-testid={`unit-${idx}-wg-${wgIdx}-remove`}>×</button>
            </span>
          ))}
        </div>
        <div className="flex gap-1">
          <input className="input-field flex-1 text-xs py-1.5" placeholder="Wargear / weapon…" value={wg} onChange={e => setWg(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { onAddWargear(wg); setWg(''); } }} data-testid={`unit-${idx}-wg-input`} />
          <button onClick={() => { onAddWargear(wg); setWg(''); }} className="px-2 py-1.5 rounded-lg bg-[#DC2626]/20 text-[#DC2626] text-xs" data-testid={`unit-${idx}-wg-add`}>+</button>
        </div>
      </div>
    </div>
  );
}
